import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import {
  ShieldCheck, Users, Building2, FileText, Trash2, KeyRound, Edit3,
  Plus, Search, X, Copy, Check, Crown, AlertTriangle, ChevronRight, Menu as MenuIcon,
  Mail, Inbox, UserCheck, UserX, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/company";
import { COUNTRIES, CURRENCIES, TAX_REGIMES, statesFor, countryDefaults } from "@/lib/geoData";

// ============================================================
// Small helpers
// ============================================================

function useConsoleData(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(path);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
      setData(null);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [path, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

function StatusChip({ status }) {
  const map = {
    active: "chip-success", pending_approval: "chip-warn", disabled: "chip-danger",
  };
  return <span className={`chip ${map[status] || "chip-neutral"}`}>{status || "active"}</span>;
}

function RoleChip({ role }) {
  const cls = role === "super_admin" ? "chip-info" : role === "admin" ? "chip-info" : "chip-neutral";
  return <span className={`chip ${cls}`}>{role}</span>;
}

function copyToClip(text) {
  navigator.clipboard?.writeText(text).then(() => toast.success("Copied"));
}

// ============================================================
// Overview tab
// ============================================================

function DomainBanner() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/platform-settings").then((r) => setS(r.data)).catch(() => setS(null));
  }, []);
  if (!s) return null;
  const url = s.public_app_url || "(not configured)";
  const isCustom = /^https?:\/\/(?!.*preview\.emergentagent\.com).+/i.test(url);
  return (
    <div
      className="card-surface p-5 relative overflow-hidden"
      data-testid="admin-domain-banner"
      style={{
        background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
        borderColor: "#1E293B",
      }}
    >
      <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-20"
             style={{ background: "radial-gradient(circle, #FF5500 0%, transparent 70%)",
                       transform: "translate(30%, -30%)" }}/>
      <div className="relative z-10 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-widest uppercase font-semibold text-[#FF7A33] mb-1">
            Your Domain
          </div>
          <div className="text-xl sm:text-2xl font-bold text-white font-mono-data break-all" data-testid="banner-app-url">
            {url}
          </div>
          <div className="text-xs text-[#94A3B8] mt-2 max-w-2xl">
            Every welcome email, invoice link and admin invite uses this URL.{" "}
            {isCustom
              ? "You're on a custom domain — professional and production-ready."
              : "Deploy the app and connect a custom domain to make outbound emails look fully professional."}
          </div>
        </div>
        <div className="text-right space-y-1 text-xs">
          <div className="flex items-center gap-2 justify-end">
            <span className="text-[#94A3B8]">Brand</span>
            <span className="text-white font-bold" data-testid="banner-brand">{s.brand_name}</span>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <span className="text-[#94A3B8]">Sender</span>
            <span className="font-mono-data text-white" data-testid="banner-sender">{s.sender_email}</span>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <span className="text-[#94A3B8]">Reply-To</span>
            <span className="font-mono-data text-white" data-testid="banner-reply-to">{s.reply_to_email}</span>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <span className="text-[#94A3B8]">Email service</span>
            <span className={`chip ${s.email_service_configured ? "chip-success" : "chip-warn"}`}>
              {s.email_service_configured ? "Live" : "Not configured"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}


function Overview({ onGoTo }) {
  const { data } = useConsoleData("/admin/overview");
  if (!data) return <div className="text-[#64748B] text-sm">Loading…</div>;
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="admin-overview-tab">
      <DomainBanner />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          ["Users", t.users, "#1E3A8A", "users"],
          ["Active", t.active, "#16A34A", "users"],
          ["Pending", t.pending, "#F59E0B", "users"],
          ["Disabled", t.disabled, "#DC2626", "users"],
          ["Companies", t.companies, "#3B82F6", "companies"],
          ["Invoices", t.invoices, "#0EA5E9", "projects"],
        ].map(([lbl, val, color, tab]) => (
          <button key={lbl} onClick={() => onGoTo?.(tab)}
            className="kpi-tile text-left hover:border-[#1E3A8A]"
            data-testid={`admin-kpi-${lbl.toLowerCase()}`}>
            <div className="text-xs text-[#64748B] mb-1">{lbl}</div>
            <div className="text-2xl font-bold font-mono-data" style={{ color }}>{val}</div>
          </button>
        ))}
      </div>
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <AlertTriangle size={16} className="text-[#F59E0B]"/> Pending Approvals
          </h3>
          <button className="text-sm text-[#1E3A8A] hover:underline" onClick={() => onGoTo?.("pending")} data-testid="overview-pending-cta">
            Manage approvals →
          </button>
        </div>
        {(data.pending_users || []).length === 0 ? (
          <div className="text-sm text-[#64748B] py-4">No pending approvals.</div>
        ) : (
          <table className="table-clean">
            <thead><tr><th>Email</th><th>Name</th><th>Company</th><th>Requested</th></tr></thead>
            <tbody>
              {data.pending_users.map((u) => (
                <tr key={u.id} data-testid={`pending-user-${u.id}`}>
                  <td className="font-mono-data">{u.email}</td>
                  <td>{u.name || "-"}</td>
                  <td>{u.company_name || "-"}</td>
                  <td className="font-mono-data text-xs">{u.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================
// User Edit / Create modal
// ============================================================

function UserModal({ open, mode, initial, companies, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setF(mode === "create"
        ? { email: "", password: "", name: "", role: "admin", company_id: "", company_name: "", send_welcome: true }
        : { name: initial?.name || "", email: initial?.email || "",
            role: initial?.role || "user", status: initial?.status || "active",
            company_id: initial?.company_id || "" });
    }
  }, [open, mode, initial]);
  if (!open) return null;
  const submit = async () => {
    setSaving(true);
    try {
      if (mode === "create") {
        await api.post("/admin/users", f);
        toast.success("User created");
      } else {
        await api.put(`/admin/users/${initial.id}`, f);
        toast.success("User updated");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="user-modal">
      <div className="bg-white rounded-lg w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-bold">{mode === "create" ? "New User" : "Edit User"}</h2>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="label-field">Email *</label>
            <input data-testid="user-modal-email" className="input-clean" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })}/></div>
          <div><label className="label-field">Name</label>
            <input data-testid="user-modal-name" className="input-clean" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })}/></div>
          {mode === "create" && (
            <div className="md:col-span-2"><label className="label-field">Temporary password *</label>
              <input data-testid="user-modal-password" type="text" className="input-clean font-mono-data" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })}/></div>
          )}
          <div><label className="label-field">Role</label>
            <select data-testid="user-modal-role" className="input-clean" value={f.role || "user"} onChange={(e) => setF({ ...f, role: e.target.value })}>
              <option value="super_admin">super_admin</option>
              <option value="admin">admin</option>
              <option value="user">user</option>
            </select></div>
          {mode === "edit" && (
            <div><label className="label-field">Status</label>
              <select data-testid="user-modal-status" className="input-clean" value={f.status || "active"} onChange={(e) => setF({ ...f, status: e.target.value })}>
                <option value="active">active</option>
                <option value="pending_approval">pending_approval</option>
                <option value="disabled">disabled</option>
              </select></div>
          )}
          <div className="md:col-span-2"><label className="label-field">Company</label>
            <select data-testid="user-modal-company" className="input-clean" value={f.company_id || ""} onChange={(e) => setF({ ...f, company_id: e.target.value })}>
              <option value="">— none —</option>
              {(companies || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          {mode === "create" && !f.company_id && (
            <div className="md:col-span-2"><label className="label-field">Or create a new company</label>
              <input className="input-clean" value={f.company_name || ""} onChange={(e) => setF({ ...f, company_name: e.target.value })} placeholder="Company name"/></div>
          )}
          {mode === "create" && (
            <div className="md:col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={!!f.send_welcome} onChange={(e) => setF({ ...f, send_welcome: e.target.checked })}/>
              <label className="text-sm text-[#64748B]">Send welcome email</label>
            </div>
          )}
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="user-modal-save" onClick={submit} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
            {saving ? "Saving…" : mode === "create" ? "Create User" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Reset password modal (shared between users + companies)
// ============================================================

function ResetPasswordModal({ open, target, kind, onClose }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setPwd(""); setResult(null); } }, [open]);
  if (!open) return null;
  const submit = async () => {
    setSaving(true);
    try {
      const path = kind === "company"
        ? `/admin/companies/${target.id}/reset-owner-password`
        : `/admin/users/${target.id}/reset-password`;
      const { data } = await api.post(path, { new_password: pwd || null });
      setResult(data);
      toast.success("Password reset");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="reset-pw-modal">
      <div className="bg-white rounded-lg w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2"><KeyRound size={16}/> Reset Password</h2>
            <p className="text-xs text-[#64748B] mt-0.5">
              {kind === "company"
                ? <>Company: <b className="text-[#0F172A]">{target?.name}</b></>
                : <>User: <b className="text-[#0F172A]">{target?.email}</b></>}
            </p>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        {!result ? (
          <>
            <label className="label-field">New password (leave blank for auto-generated)</label>
            <input data-testid="reset-pw-input" type="text" className="input-clean font-mono-data" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Auto-generate…"/>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
              <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
              <button data-testid="reset-pw-submit" onClick={submit} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
                {saving ? "Resetting…" : "Reset Password"}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3" data-testid="reset-pw-result">
            <div className="p-3 rounded bg-[#DBEAFE] border border-[#1E3A8A]/20">
              <div className="text-xs text-[#64748B] mb-1">Temporary password for <b className="font-mono-data">{result.email}</b></div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded font-mono-data text-[#1E3A8A] font-bold">{result.temporary_password}</code>
                <button onClick={() => copyToClip(result.temporary_password)} className="btn-secondary">
                  <Copy size={14}/> Copy
                </button>
              </div>
              <div className="text-xs text-[#DC2626] mt-2 flex items-center gap-1">
                <AlertTriangle size={12}/> Share securely. User should change on first login.
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="btn-primary">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Users tab
// ============================================================

function UsersTab({ companies }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const { data, reload } = useConsoleData(
    `/admin/users?${q ? `q=${encodeURIComponent(q)}&` : ""}${status ? `status=${status}&` : ""}limit=500`,
    [q, status]
  );
  const [selected, setSelected] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [modalTarget, setModalTarget] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const users = data?.users || [];

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === users.length) setSelected(new Set());
    else setSelected(new Set(users.map((u) => u.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} user(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} deleted, ${data.skipped} skipped`);
      setSelected(new Set());
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const approve = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { status: "active" });
      toast.success("Approved");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-[#94A3B8]"/>
            <input data-testid="admin-users-search" className="input-clean pl-7" placeholder="Search email or name" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 260 }}/>
          </div>
          <select className="input-clean" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 180 }} data-testid="admin-users-status-filter">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending_approval">Pending</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <button data-testid="admin-users-bulk-delete" onClick={bulkDelete} className="btn-danger">
              <Trash2 size={14}/> Delete {selected.size}
            </button>
          )}
          <button data-testid="admin-user-new" onClick={() => { setModalMode("create"); setModalTarget(null); setModalOpen(true); }} className="btn-primary">
            <Plus size={14}/> New User
          </button>
        </div>
      </div>

      <div className="card-surface p-4">
        <table className="table-clean">
          <thead>
            <tr>
              <th style={{ width: 30 }}>
                <input type="checkbox" checked={users.length > 0 && selected.size === users.length} onChange={toggleAll} data-testid="admin-users-select-all"/>
              </th>
              <th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>Company</th><th></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[#64748B]">No users match.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <td>
                  <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} data-testid={`admin-user-select-${u.id}`}/>
                </td>
                <td className="font-mono-data text-sm">
                  {u.email}
                  {u.role === "super_admin" && <Crown size={12} className="inline ml-1 text-[#F59E0B]"/>}
                </td>
                <td>{u.name || "-"}</td>
                <td><RoleChip role={u.role}/></td>
                <td><StatusChip status={u.status}/></td>
                <td className="text-sm">{u.company_name || "-"}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    {u.status === "pending_approval" && (
                      <button onClick={() => approve(u)} className="text-[#16A34A] text-xs font-semibold" data-testid={`admin-approve-${u.id}`}>
                        <Check size={14}/>
                      </button>
                    )}
                    <button onClick={() => { setPwTarget(u); setPwOpen(true); }} className="text-[#1E3A8A]" data-testid={`admin-reset-pw-${u.id}`} title="Reset password">
                      <KeyRound size={14}/>
                    </button>
                    <button onClick={() => { setModalMode("edit"); setModalTarget(u); setModalOpen(true); }} className="text-[#64748B]" data-testid={`admin-edit-user-${u.id}`}>
                      <Edit3 size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserModal open={modalOpen} mode={modalMode} initial={modalTarget} companies={companies} onClose={() => setModalOpen(false)} onSaved={reload}/>
      <ResetPasswordModal open={pwOpen} target={pwTarget} kind="user" onClose={() => { setPwOpen(false); }}/>
    </div>
  );
}

// ============================================================
// Company Edit Modal
// ============================================================

function CompanyModal({ open, initial, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF({
      name: initial?.name || "",
      country: initial?.country || "IN",
      base_currency: initial?.base_currency || "INR",
      tax_regime: initial?.tax_regime || "IN_GST",
      gstin: initial?.gstin || "", vat_id: initial?.vat_id || "",
      state: initial?.state || "", state_code: initial?.state_code || "",
      city: initial?.city || "", postal_code: initial?.postal_code || "",
      address_line1: initial?.address_line1 || "",
    });
  }, [open, initial]);
  if (!open) return null;

  const onCountryChange = (code) => {
    const c = countryDefaults(code);
    setF((s) => ({
      ...s, country: code,
      base_currency: c?.currency || s.base_currency,
      tax_regime: c?.regime || s.tax_regime,
      state: "", state_code: "",
      gstin: code === "IN" ? s.gstin : "",
      vat_id: code !== "IN" ? s.vat_id : "",
    }));
  };

  const states = statesFor(f.country);
  const isGst = f.tax_regime === "IN_GST";
  const isVat = ["AE_VAT", "EU_VAT", "UK_VAT"].includes(f.tax_regime);

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/companies/${initial.id}`, f);
      toast.success("Company updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="company-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-bold">Edit Company</h2>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><label className="label-field">Company Name</label>
            <input data-testid="company-modal-name" className="input-clean" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })}/></div>
          <div><label className="label-field">Country</label>
            <select data-testid="company-modal-country" className="input-clean" value={f.country || "IN"} onChange={(e) => onCountryChange(e.target.value)}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select></div>
          <div><label className="label-field">Base Currency</label>
            <select data-testid="company-modal-currency" className="input-clean" value={f.base_currency || ""} onChange={(e) => setF({ ...f, base_currency: e.target.value })}>
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select></div>
          <div><label className="label-field">Tax Regime</label>
            <select data-testid="company-modal-regime" className="input-clean" value={f.tax_regime || "NONE"} onChange={(e) => setF({ ...f, tax_regime: e.target.value })}>
              {TAX_REGIMES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select></div>
          <div><label className="label-field">State / Region</label>
            {states.length > 0 ? (
              <select data-testid="company-modal-state" className="input-clean" value={f.state_code || ""}
                onChange={(e) => {
                  const st = states.find(([code]) => code === e.target.value);
                  setF({ ...f, state_code: e.target.value, state: st ? st[1] : "" });
                }}>
                <option value="">Select state…</option>
                {states.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
            ) : (
              <input data-testid="company-modal-state" className="input-clean" value={f.state || ""} onChange={(e) => setF({ ...f, state: e.target.value, state_code: e.target.value })}/>
            )}
          </div>
          {isGst && (
            <div><label className="label-field">GSTIN</label>
              <input data-testid="company-modal-gstin" className="input-clean font-mono-data" maxLength={15} value={f.gstin || ""} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })}/></div>
          )}
          {isVat && (
            <div><label className="label-field">VAT ID</label>
              <input data-testid="company-modal-vat" className="input-clean font-mono-data" value={f.vat_id || ""} onChange={(e) => setF({ ...f, vat_id: e.target.value })}/></div>
          )}
          <div><label className="label-field">City</label>
            <input className="input-clean" value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })}/></div>
          <div><label className="label-field">Postal Code</label>
            <input className="input-clean" value={f.postal_code || ""} onChange={(e) => setF({ ...f, postal_code: e.target.value })}/></div>
          <div className="md:col-span-2"><label className="label-field">Address</label>
            <input className="input-clean" value={f.address_line1 || ""} onChange={(e) => setF({ ...f, address_line1: e.target.value })}/></div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="company-modal-save" onClick={submit} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Companies tab
// ============================================================

function CompaniesTab() {
  const { data, reload } = useConsoleData("/admin/companies");
  const [selected, setSelected] = useState(new Set());
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [q, setQ] = useState("");

  const companies = useMemo(() => {
    const rows = data?.companies || [];
    return q ? rows.filter((c) => (c.name || "").toLowerCase().includes(q.toLowerCase())) : rows;
  }, [data, q]);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === companies.length) setSelected(new Set());
    else setSelected(new Set(companies.map((c) => c.id)));
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} company/ies and ALL their data? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/companies/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} deleted`);
      setSelected(new Set());
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteOne = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and ALL its data?`)) return;
    try {
      await api.delete(`/admin/companies/${c.id}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-companies-tab">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-2.5 text-[#94A3B8]"/>
          <input data-testid="admin-companies-search" className="input-clean pl-7" placeholder="Search company name" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 260 }}/>
        </div>
        {selected.size > 0 && (
          <button data-testid="admin-companies-bulk-delete" onClick={bulkDelete} className="btn-danger">
            <Trash2 size={14}/> Delete {selected.size}
          </button>
        )}
      </div>

      <div className="card-surface p-4">
        <table className="table-clean">
          <thead>
            <tr>
              <th style={{ width: 30 }}>
                <input type="checkbox" checked={companies.length > 0 && selected.size === companies.length} onChange={toggleAll} data-testid="admin-companies-select-all"/>
              </th>
              <th>Company</th><th>Country</th><th>Currency</th><th>Regime</th><th>GSTIN/VAT</th><th>Members</th><th></th>
            </tr>
          </thead>
          <tbody>
            {companies.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-[#64748B]">No companies.</td></tr>}
            {companies.map((c) => (
              <tr key={c.id} data-testid={`admin-company-row-${c.id}`}>
                <td>
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} data-testid={`admin-company-select-${c.id}`}/>
                </td>
                <td className="font-medium">{c.name}</td>
                <td className="font-mono-data text-xs">{c.country || "-"}</td>
                <td className="font-mono-data text-xs">{c.base_currency || "-"}</td>
                <td className="font-mono-data text-xs">{c.tax_regime || "-"}</td>
                <td className="font-mono-data text-xs">{c.gstin || c.vat_id || "-"}</td>
                <td className="text-sm">{c.member_count ?? "-"}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => { setPwTarget(c); setPwOpen(true); }} className="text-[#1E3A8A]" data-testid={`admin-reset-owner-${c.id}`} title="Reset owner password">
                      <KeyRound size={14}/>
                    </button>
                    <button onClick={() => { setEditTarget(c); setEditOpen(true); }} className="text-[#64748B]" data-testid={`admin-edit-company-${c.id}`}>
                      <Edit3 size={14}/>
                    </button>
                    <button onClick={() => deleteOne(c)} className="text-[#DC2626]" data-testid={`admin-delete-company-${c.id}`}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CompanyModal open={editOpen} initial={editTarget} onClose={() => setEditOpen(false)} onSaved={reload}/>
      <ResetPasswordModal open={pwOpen} target={pwTarget} kind="company" onClose={() => { setPwOpen(false); }}/>
    </div>
  );
}

// ============================================================
// Projects tab (cross-tenant activity)
// ============================================================

function ProjectsTab() {
  const [q, setQ] = useState("");
  const { data } = useConsoleData(`/admin/projects?${q ? `q=${encodeURIComponent(q)}&` : ""}limit=200`, [q]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { data: invData } = useConsoleData(
    selectedCompany ? `/admin/invoices?company_id=${selectedCompany.id}&limit=100` : "/admin/invoices?limit=100",
    [selectedCompany]
  );
  const rows = data?.projects || [];
  const invoices = invData?.invoices || [];

  const deleteInv = async (id) => {
    if (!window.confirm("Force-delete this invoice across tenants?")) return;
    try {
      await api.delete(`/admin/invoices/${id}`);
      toast.success("Deleted");
      // trigger reload via re-set
      setSelectedCompany(selectedCompany ? { ...selectedCompany } : null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-projects-tab">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2 top-2.5 text-[#94A3B8]"/>
          <input data-testid="admin-projects-search" className="input-clean pl-7" placeholder="Search company name" value={q} onChange={(e) => setQ(e.target.value)}/>
        </div>
        {selectedCompany && (
          <button onClick={() => setSelectedCompany(null)} className="btn-ghost">
            <X size={14}/> Clear filter · {selectedCompany.name}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left — workspaces */}
        <div className="lg:col-span-5 card-surface p-4 max-h-[70vh] overflow-y-auto">
          <div className="overline mb-3">Workspaces</div>
          {rows.length === 0 && <div className="text-sm text-[#64748B] py-6 text-center">No workspaces.</div>}
          {rows.map((p) => (
            <button
              key={p.id} data-testid={`project-card-${p.id}`}
              onClick={() => setSelectedCompany(p)}
              className={`w-full text-left p-3 border rounded-md mb-2 hover:border-[#1E3A8A] flex items-start gap-3 ${selectedCompany?.id === p.id ? "border-[#1E3A8A] bg-[#DBEAFE]" : "border-[#E2E8F0]"}`}
            >
              <Building2 size={16} className="text-[#1E3A8A] mt-0.5 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#0F172A]">{p.name}</div>
                <div className="text-[11px] text-[#64748B] font-mono-data">{p.country} · {p.base_currency} · {p.tax_regime}</div>
                <div className="flex gap-3 text-[11px] text-[#64748B] mt-1 flex-wrap">
                  <span>Members <b className="text-[#0F172A]">{p.counts.members}</b></span>
                  <span>Invoices <b className="text-[#0F172A]">{p.counts.invoices}</b></span>
                  <span>Items <b className="text-[#0F172A]">{p.counts.items}</b></span>
                  <span>Parties <b className="text-[#0F172A]">{p.counts.parties}</b></span>
                  <span>Expenses <b className="text-[#0F172A]">{p.counts.expenses}</b></span>
                </div>
              </div>
              <ChevronRight size={14} className="text-[#94A3B8] mt-1"/>
            </button>
          ))}
        </div>

        {/* Right — invoice activity */}
        <div className="lg:col-span-7 card-surface p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">
              {selectedCompany ? `Invoices · ${selectedCompany.name}` : "All Invoices (recent)"}
            </div>
          </div>
          {invoices.length === 0 ? (
            <div className="text-sm text-[#64748B] py-8 text-center">No invoices.</div>
          ) : (
            <div className="max-h-[65vh] overflow-y-auto">
              <table className="table-clean">
                <thead>
                  <tr>
                    <th>#</th><th>Company</th><th>Party</th><th>Date</th>
                    <th className="text-right">Total</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} data-testid={`admin-invoice-row-${i.id}`}>
                      <td className="font-mono-data text-xs">{i.number}</td>
                      <td className="text-xs">{i.company_name}</td>
                      <td className="text-sm">{i.party}</td>
                      <td className="font-mono-data text-xs">{i.date}</td>
                      <td className="text-right font-mono-data font-semibold">{fmtMoney(i.total, i.currency)}</td>
                      <td>
                        <span className={`chip chip-${i.status === "paid" ? "success" : i.status === "draft" ? "neutral" : "warn"} text-[10px]`}>{i.status}</span>
                      </td>
                      <td>
                        <button onClick={() => deleteInv(i.id)} className="text-[#DC2626]" data-testid={`admin-delete-invoice-${i.id}`}>
                          <Trash2 size={12}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Leads Tab — landing form + careers form submissions
// ============================================================

function LeadsTab() {
  const [rows, setRows] = useState([]);
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source) params.set("source", source);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/leads?${params}`);
      setRows(data.leads || []);
      setSources(data.sources || []);
    } catch (e) { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  }, [source, q]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (l) => {
    try {
      await api.post(`/admin/leads/${l.id}/read`);
      setRows((rs) => rs.map((r) => r.id === l.id ? { ...r, read: true } : r));
    } catch { /* non-critical */ }
  };

  const del = async (l) => {
    if (!window.confirm(`Delete lead from ${l.name}?`)) return;
    try {
      await api.delete(`/admin/leads/${l.id}`);
      toast.success("Deleted");
      setSelected(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDetail = (l) => {
    setSelected(l);
    if (!l.read) markRead(l);
  };

  const src = source || "all";

  return (
    <div className="space-y-4" data-testid="admin-leads-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-2 top-2.5 text-[#94A3B8]"/>
          <input
            data-testid="leads-search" className="input-clean pl-7"
            placeholder="Search name / email / company / message"
            value={q} onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex gap-1">
          <button
            data-testid="leads-source-all"
            onClick={() => setSource("")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${src === "all" ? "bg-[#0F172A] border-[#0F172A] text-white" : "bg-white border-[#E2E8F0] text-[#0F172A] hover:border-[#FF5500]"}`}
          >
            All ({rows.length})
          </button>
          {sources.map((s) => (
            <button
              key={s}
              data-testid={`leads-source-${s}`}
              onClick={() => setSource(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize ${source === s ? "bg-[#0F172A] border-[#0F172A] text-white" : "bg-white border-[#E2E8F0] text-[#0F172A] hover:border-[#FF5500]"}`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 card-surface p-0 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#64748B]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <Inbox size={44} className="mx-auto text-[#CBD5E1] mb-3"/>
              <div className="text-sm text-[#64748B]">
                {q ? "No leads match your search." : "No leads captured yet."}
              </div>
            </div>
          ) : (
            <table className="table-clean">
              <thead>
                <tr><th></th><th>Name</th><th>Company</th><th>Source</th><th>Received</th></tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id} onClick={() => openDetail(l)}
                    className={`cursor-pointer ${selected?.id === l.id ? "bg-[#FFEDE0]" : ""}`}
                    data-testid={`lead-row-${l.id}`}>
                    <td>{!l.read && <span className="inline-block w-2 h-2 rounded-full bg-[#FF5500]" title="Unread"/>}</td>
                    <td>
                      <div className={`text-sm ${l.read ? "text-[#64748B]" : "font-bold text-[#0F172A]"}`}>{l.name}</div>
                      <div className="text-[11px] text-[#64748B] font-mono-data">{l.email}</div>
                    </td>
                    <td className="text-sm">{l.company || <span className="text-[#94A3B8]">—</span>}</td>
                    <td><span className="chip chip-neutral capitalize text-[10px]">{l.source?.replace("_", " ")}</span></td>
                    <td className="text-xs font-mono-data text-[#64748B]">{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="lg:col-span-5 card-surface p-5" data-testid="lead-detail">
          {!selected ? (
            <div className="py-16 text-center text-[#64748B]">
              <Mail size={40} className="mx-auto mb-2 text-[#CBD5E1]"/>
              Select a lead to view details.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="overline mb-1">{selected.source?.replace("_", " ")}</div>
                  <h3 className="text-lg font-bold text-[#0F172A]">{selected.name}</h3>
                  <a href={`mailto:${selected.email}`} className="text-sm text-[#FF5500] hover:underline">{selected.email}</a>
                </div>
                <button onClick={() => del(selected)} className="text-[#DC2626]" title="Delete" data-testid="lead-delete">
                  <Trash2 size={16}/>
                </button>
              </div>
              <div className="space-y-2 text-sm">
                {selected.company && <div><span className="overline">Company</span><div className="text-[#0F172A]">{selected.company}</div></div>}
                {selected.country && <div><span className="overline">Country</span><div className="text-[#0F172A]">{selected.country}</div></div>}
                <div><span className="overline">Received</span>
                  <div className="text-[#0F172A] font-mono-data text-xs">
                    {new Date(selected.created_at).toLocaleString()} · IP {selected.ip}
                  </div></div>
                <div className="pt-2 mt-2 border-t border-[#E2E8F0]">
                  <span className="overline">Message</span>
                  <div className="text-[#0F172A] whitespace-pre-wrap mt-1 text-sm leading-relaxed">
                    {selected.message || <span className="text-[#94A3B8] italic">(no message)</span>}
                  </div>
                </div>
              </div>
              <a
                href={`mailto:${selected.email}?subject=Re: your enquiry to SERP`}
                data-testid="lead-reply-btn"
                className="btn-primary mt-4 w-full justify-center"
              >
                <Mail size={14}/> Reply via Email
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Approval Requests Tab — invoices / POs / payments above thresholds
// ============================================================

function ApprovalsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/approvals/pending"); setItems(data.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (r, verdict) => {
    const reason = window.prompt(verdict === "approved" ? "Approval note (optional)" : "Reason for rejection", "");
    if (verdict === "rejected" && reason === null) return;
    setBusy(r.id);
    try {
      await api.post(`/approvals/${r.id}/${verdict === "approved" ? "approve" : "reject"}`, { reason: reason || null });
      toast.success(`Request ${verdict}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const fmt = (amt, cur) => {
    const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" }[cur] || (cur + " ");
    return `${sym}${Number(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-4" data-testid="admin-approvals-tab">
      <div className="card-surface p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div>
            <div className="overline mb-1">Approval Gate</div>
            <h2 className="text-lg sm:text-xl font-bold text-[#0F172A]">Pending Approvals · Invoices / POs / Payments</h2>
            <div className="text-sm text-[#64748B] mt-1">
              Documents above the configured company threshold wait here for Super Admin approval before they post to the ledger.
            </div>
          </div>
          <button onClick={load} className="btn-ghost w-full sm:w-auto justify-center" data-testid="approvals-refresh-btn">Refresh</button>
        </div>
        {loading ? <div className="py-6 text-sm text-[#64748B]">Loading…</div>
          : items.length === 0 ? <div className="py-8 text-center text-sm text-[#64748B]" data-testid="approvals-empty">Inbox zero — nothing needs your approval.</div>
          : (<>
            <div className="md:hidden space-y-3">
              {items.map((r) => (
                <div key={r.id} className="border border-[#E2E8F0] rounded-lg p-3 bg-white" data-testid={`approval-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-mono-data font-semibold text-[#0F172A] text-sm">{r.doc_number}</div>
                      <div className="text-sm truncate">{r.party_name || "—"}</div>
                      <div className="text-[11px] text-[#64748B] mt-0.5">{r.company_name} · {r.doc_kind.replace("_"," ")}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-data font-bold text-[#0F172A]">{fmt(r.amount, r.currency)}</div>
                      <div className="text-[10px] text-[#64748B] mt-1">{r.requested_by_email}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-[#F1F5F9]">
                    <button disabled={busy === r.id} onClick={() => decide(r, "approved")} className="btn-primary flex-1 justify-center" data-testid={`approve-${r.id}`}><CheckCircle2 size={14}/> Approve</button>
                    <button disabled={busy === r.id} onClick={() => decide(r, "rejected")} className="btn-danger flex-1 justify-center" data-testid={`reject-${r.id}`}><X size={14}/> Reject</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block">
              <table className="table-clean">
                <thead><tr><th>Company</th><th>Doc</th><th>Kind</th><th>Party</th><th className="text-right">Amount</th><th>Requested by</th><th></th></tr></thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} data-testid={`approval-row-${r.id}`}>
                      <td>{r.company_name}</td>
                      <td className="font-mono-data text-xs font-semibold">{r.doc_number}</td>
                      <td className="text-xs">{r.doc_kind.replace("_"," ")}</td>
                      <td>{r.party_name || "—"}</td>
                      <td className="text-right font-mono-data font-semibold">{fmt(r.amount, r.currency)}</td>
                      <td className="text-xs font-mono-data">{r.requested_by_email}</td>
                      <td>
                        <div className="flex gap-2 justify-end">
                          <button disabled={busy === r.id} onClick={() => decide(r, "approved")} className="btn-primary py-1 px-2 text-xs" data-testid={`approve-desktop-${r.id}`}><CheckCircle2 size={12}/> Approve</button>
                          <button disabled={busy === r.id} onClick={() => decide(r, "rejected")} className="btn-danger py-1 px-2 text-xs" data-testid={`reject-desktop-${r.id}`}><X size={12}/> Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>)}
      </div>
    </div>
  );
}


// ============================================================
// Pending Approvals Tab — new-user approval gate
// ============================================================

function PendingApprovalsTab() {
  const { data, loading, reload } = useConsoleData("/admin/users/pending");
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null); // user obj or null
  const items = data?.items || [];

  const approve = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/approve`);
      toast.success(
        data?.email_sent
          ? `${u.email} approved — welcome email sent`
          : `${u.email} approved (email delivery pending)`,
      );
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally { setBusyId(null); }
  };

  const doReject = async (reason) => {
    const u = rejecting;
    setRejecting(null);
    if (!u) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/reject`, { reason: reason || "" });
      toast.success(`${u.email} rejected and removed`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejection failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-pending-tab">
      <div className="card-surface p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div>
            <div className="overline mb-1">Approval Gate</div>
            <h2 className="text-lg sm:text-xl font-bold text-[#0F172A]">Pending Approvals</h2>
            <div className="text-sm text-[#64748B] mt-1">
              Every new SERP account — self-signups, invited staff, company owners — is held here
              until you approve them. Only the Super Admin is exempt.
            </div>
          </div>
          <button onClick={reload} className="btn-ghost w-full sm:w-auto justify-center" data-testid="pending-refresh-btn">
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-[#64748B] py-4">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[#64748B] py-6 text-center" data-testid="pending-empty">
            All caught up — no accounts are waiting for approval.
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {items.map((u) => (
                <div key={u.id} className="border border-[#E2E8F0] rounded-lg p-3 bg-white" data-testid={`pending-card-${u.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F172A] truncate">{u.name || u.email}</div>
                      <div className="text-xs text-[#64748B] font-mono-data truncate">{u.email}</div>
                      <div className="text-[11px] text-[#94A3B8] mt-1">
                        {u.company_name || "—"} · <span className="font-mono-data">{u.role}</span>
                      </div>
                    </div>
                    <StatusChip status="pending_approval"/>
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-[#F1F5F9]">
                    <button
                      data-testid={`pending-approve-${u.id}`}
                      disabled={busyId === u.id}
                      onClick={() => approve(u)}
                      className="btn-primary flex-1 justify-center"
                    >
                      <UserCheck size={14}/> Approve
                    </button>
                    <button
                      data-testid={`pending-reject-${u.id}`}
                      disabled={busyId === u.id}
                      onClick={() => setRejecting(u)}
                      className="btn-danger flex-1 justify-center"
                    >
                      <UserX size={14}/> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="table-clean">
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Company</th><th>Role</th><th>Signed up</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u) => (
                    <tr key={u.id} data-testid={`pending-user-${u.id}`}>
                      <td className="font-medium text-[#0F172A]">{u.name || "—"}</td>
                      <td className="font-mono-data text-xs">{u.email}</td>
                      <td>{u.company_name || "—"}</td>
                      <td><RoleChip role={u.role}/></td>
                      <td className="font-mono-data text-xs text-[#64748B]">
                        {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                      </td>
                      <td>
                        <div className="flex gap-2 justify-end">
                          <button
                            data-testid={`pending-approve-desktop-${u.id}`}
                            disabled={busyId === u.id}
                            onClick={() => approve(u)}
                            className="btn-primary py-1 px-3 text-xs"
                          >
                            <UserCheck size={12}/> Approve
                          </button>
                          <button
                            data-testid={`pending-reject-desktop-${u.id}`}
                            disabled={busyId === u.id}
                            onClick={() => setRejecting(u)}
                            className="btn-danger py-1 px-3 text-xs"
                          >
                            <UserX size={12}/> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {rejecting && (
        <RejectReasonModal
          user={rejecting}
          onCancel={() => setRejecting(null)}
          onConfirm={doReject}
        />
      )}
    </div>
  );
}

function RejectReasonModal({ user, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}
        data-testid="reject-modal"
      >
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={18} className="text-[#DC2626]"/>
          <h3 className="font-semibold text-base">Reject {user.email}?</h3>
        </div>
        <p className="text-sm text-[#4B5563] mb-3">
          The account will be permanently removed. The person can register again later with the same email.
        </p>
        <label className="label-field">Reason (internal note, optional)</label>
        <textarea
          data-testid="reject-reason-input"
          className="input-clean" rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Duplicate registration / Unrecognised organisation"
        />
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <button className="btn-ghost w-full sm:w-auto justify-center" onClick={onCancel}>Cancel</button>
          <button
            data-testid="reject-confirm-btn"
            className="btn-danger w-full sm:w-auto justify-center"
            onClick={() => onConfirm(reason)}
          >
            <UserX size={14}/> Reject &amp; Remove
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Menu Management tab (super admin toggles per-company sidebar)
// ============================================================

const MENU_LABELS = {
  dashboard: "Dashboard", invoices: "Invoices", purchases: "Purchases",
  job_cards: "Job Cards", vouchers: "Vouchers",
  recurring_vouchers: "Recurring Vouchers",
  ledgers: "Ledgers",
  books: "Reports", items: "Items", parties: "Parties", expenses: "Expenses",
  reports: "Analytics", settings: "Settings",
};

function MenuManagementTab() {
  const { data: cRes, loading } = useConsoleData("/admin/companies");
  const companies = cRes?.companies || [];
  const [selected, setSelected] = useState(null);
  const [menus, setMenus] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    return q ? companies.filter((c) => (c.name || "").toLowerCase().includes(q.toLowerCase())) : companies;
  }, [companies, q]);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/companies/${selected.id}/menus`);
        setMenus(data.menus || {});
      } catch (e) { toast.error("Failed to load menus"); }
    })();
  }, [selected]);

  const toggle = (k) => setMenus((m) => ({ ...m, [k]: !m[k] }));

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/admin/companies/${selected.id}/menus`, { enabled: menus });
      toast.success("Menu settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="admin-menus-tab">
      <div className="lg:col-span-4 card-surface p-4 max-h-[70vh] overflow-y-auto">
        <div className="relative mb-3">
          <Search size={14} className="absolute left-2 top-2.5 text-[#94A3B8]"/>
          <input className="input-clean pl-7" placeholder="Search company" value={q} onChange={(e) => setQ(e.target.value)} data-testid="menus-search"/>
        </div>
        {loading && <div className="text-sm text-[#64748B]">Loading…</div>}
        {filtered.map((c) => (
          <button key={c.id} data-testid={`menus-company-${c.id}`}
            onClick={() => setSelected(c)}
            className={`w-full text-left p-3 border rounded-lg mb-2 hover:border-[#FF5500] flex items-center gap-2 ${selected?.id === c.id ? "border-[#FF5500] bg-[#FFEDE0]" : "border-[#E2E8F0]"}`}>
            <Building2 size={14} className="text-[#FF5500]"/>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#0F172A] truncate">{c.name}</div>
              <div className="text-[11px] text-[#64748B] font-mono-data">{c.country} · {c.tax_regime}</div>
            </div>
            <ChevronRight size={14} className="text-[#94A3B8]"/>
          </button>
        ))}
      </div>
      <div className="lg:col-span-8 card-surface p-6">
        {!selected ? (
          <div className="py-16 text-center text-[#64748B]">
            <MenuIcon size={40} className="mx-auto mb-2 text-[#CBD5E1]"/>
            Select a company to configure sidebar menus.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline mb-1">Menu Management</div>
                <h3 className="text-lg font-bold text-[#0F172A]">{selected.name}</h3>
                <div className="text-xs text-[#64748B] mt-0.5">Enable/disable sidebar items for this workspace only.</div>
              </div>
              <button className="btn-primary" onClick={save} disabled={saving} data-testid="menus-save">
                {saving ? "Saving…" : <><Check size={14}/> Save</>}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(MENU_LABELS).map(([key, label]) => {
                const enabled = menus[key] !== false;
                const locked = key === "dashboard";
                return (
                  <label key={key} data-testid={`menu-toggle-${key}`}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${enabled ? "border-[#FF5500] bg-[#FFF7F0]" : "border-[#E2E8F0]"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <input type="checkbox" checked={enabled} disabled={locked}
                      onChange={() => !locked && toggle(key)}
                      className="w-4 h-4 accent-[#FF5500]"/>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-[#0F172A]">{label}</div>
                      <div className="text-[11px] text-[#64748B]">{enabled ? "Visible in sidebar" : "Hidden"} {locked && "· always on"}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Root
// ============================================================

export default function SuperAdminConsole() {
  const { isSuperAdmin, user, ready } = useAuth();
  const [tab, setTab] = useState("overview");
  const { data: companiesData } = useConsoleData("/admin/companies");
  const companies = companiesData?.companies || [];

  if (!ready) return null;
  if (!isSuperAdmin) return <Navigate to="/app/dashboard" replace/>;

  const tabs = [
    { key: "overview", label: "Overview", icon: ShieldCheck },
    { key: "pending", label: "User Approvals", icon: UserCheck },
    { key: "approvals", label: "Doc Approvals", icon: ShieldCheck },
    { key: "users", label: "Users", icon: Users },
    { key: "companies", label: "Companies", icon: Building2 },
    { key: "menus", label: "Menu Management", icon: MenuIcon },
    { key: "leads", label: "Leads", icon: Inbox },
    { key: "projects", label: "Projects", icon: FileText },
  ];

  return (
    <div className="space-y-6" data-testid="super-admin-console">
      {/* Header banner */}
      <div className="card-surface p-5 flex items-center gap-4"
        style={{ background: "linear-gradient(90deg, #0F172A 0%, #1E3A8A 100%)", borderColor: "#1E3A8A" }}
      >
        <div className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
          <Crown size={22} className="text-[#FCD34D]"/>
        </div>
        <div className="flex-1">
          <div className="text-[11px] text-[#93C5FD] font-mono-data uppercase tracking-widest mb-1">
            Platform · Restricted access
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Super Admin Console</h1>
          <div className="text-xs sm:text-sm text-white/70 mt-0.5 truncate">Signed in as <b className="font-mono-data">{user?.email}</b></div>
        </div>
      </div>

      {/* Tabs — horizontal scroll on mobile so all fit */}
      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0" data-testid="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t.key} data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap shrink-0 ${tab === t.key ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}
            style={{ minHeight: 44 }}
          >
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview onGoTo={setTab}/>}
      {tab === "pending" && <PendingApprovalsTab/>}
      {tab === "users" && <UsersTab companies={companies}/>}
      {tab === "companies" && <CompaniesTab/>}
      {tab === "menus" && <MenuManagementTab/>}
      {tab === "leads" && <LeadsTab/>}
      {tab === "projects" && <ProjectsTab/>}
    </div>
  );
}
